
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { getCustomers } from "../services/customerService";
import userService from "../services/userService";
import trackService from "../services/trackService";
import ProgressReportSection from "../components/Dashboard/ProgressReportSection";

/**
 * Home page component with dashboard metrics
 */
const Home = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dashboard data with initial empty values
  const [dashboardData, setDashboardData] = useState({
    customers: {
      count: 0,
      label: "Total registered customers"
    },
    activeUsers: {
      count: 0,
      label: "Users across all tenants"
    },
    learningTracks: {
      count: 0,
      label: "Available learning tracks"
    }
  });

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch customers
        const customersData = await getCustomers();
        
        // Fetch users
        const usersData = await userService.getUsers();
        
        // Fetch tracks
        const tracksData = await trackService.getTracks();
        
        // Update dashboard data based on API responses
        setDashboardData(prevData => ({
          ...prevData,
          customers: {
            ...prevData.customers,
            count: Array.isArray(customersData) ? customersData.length : 0
          },
          activeUsers: {
            ...prevData.activeUsers,
            count: Array.isArray(usersData) ? usersData.length : 0
          },
          learningTracks: {
            ...prevData.learningTracks,
            count: Array.isArray(tracksData) ? tracksData.length : 0
          }
        }));
        
        setError(null);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="page-container">
      <h1 className="dashboard-title">Dashboard</h1>
      
      <div className="dashboard-grid">
        {/* Customers Card */}
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h2 className="dashboard-card-title">Customers</h2>
            <div className="dashboard-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
          </div>
          {loading ? (
            <div className="flex justify-center items-center p-md">
              <div className="spinner"></div>
            </div>
          ) : error ? (
            <div className="text-error p-sm">{error}</div>
          ) : (
            <>
              <div className="dashboard-card-value">{dashboardData.customers.count}</div>
              <div className="dashboard-card-label">{dashboardData.customers.label}</div>
            </>
          )}
          <div className="dashboard-card-footer">
            <Link to="/customers" className="dashboard-card-link">
              View all
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </Link>
          </div>
        </div>
        
        {/* Active Users Card */}
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h2 className="dashboard-card-title">Active Users</h2>
            <div className="dashboard-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
          </div>
          {loading ? (
            <div className="flex justify-center items-center p-md">
              <div className="spinner"></div>
            </div>
          ) : error ? (
            <div className="text-error p-sm">{error}</div>
          ) : (
            <>
              <div className="dashboard-card-value">{dashboardData.activeUsers.count}</div>
              <div className="dashboard-card-label">{dashboardData.activeUsers.label}</div>
            </>
          )}
          <div className="dashboard-card-footer">
            <Link to="/users" className="dashboard-card-link">
              View all users
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </Link>
          </div>
        </div>
        
        {/* Learning Tracks Card */}
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h2 className="dashboard-card-title">Learning Tracks</h2>
            <div className="dashboard-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
              </svg>
            </div>
          </div>
          {loading ? (
            <div className="flex justify-center items-center p-md">
              <div className="spinner"></div>
            </div>
          ) : error ? (
            <div className="text-error p-sm">{error}</div>
          ) : (
            <>
              <div className="dashboard-card-value">{dashboardData.learningTracks.count}</div>
              <div className="dashboard-card-label">{dashboardData.learningTracks.label}</div>
            </>
          )}
          <div className="dashboard-card-footer">
            <Link to="/tracks" className="dashboard-card-link">
              View all tracks
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </Link>
          </div>
        </div>
        
      </div>
      
      {/* Progress Report Section */}
      <ProgressReportSection />
    </div>
  );
};

export default Home;
